import { getRef } from '@analytics/analyticsRef';
import { ErrorCodes } from '@constants/ErrorCodes';
import { MeetingTemplateName } from '@features/meetingTemplates/templateData';
import { ApiError } from '@src/errors/ApiError';
import i18n from '@src/i18n';
import { logger } from '@utils/logger';
import { EventEmitter } from 'events';
import toast from 'react-hot-toast';

import { RoomStateCacheApi } from './roomState/RoomStateCacheApi';
import { RoomStateStore, roomStateStore } from './roomState/roomStateStore';
import { SocketConnection } from './roomState/SocketConnection';
import { IncomingAuthResponseMessage } from './roomState/types/socketProtocol';
import { getServices } from './services';
import { Actor, BaseResponse, ErrorResponse, Group, GroupRoom, Room } from './types';

const SESSION_TOKEN_KEY = 'ndl_token';

export type Service = {
  http: string;
  ws?: string;
};

export interface ApiCoreClientEvents {
  actorChange: (actor: Actor | null) => void;
}

export declare interface ApiCoreClient {
  on<Event extends keyof ApiCoreClientEvents>(ev: Event, cb: ApiCoreClientEvents[Event]): this;
  off<Event extends keyof ApiCoreClientEvents>(ev: Event, cb: ApiCoreClientEvents[Event]): this;
  emit<Event extends keyof ApiCoreClientEvents>(ev: Event, ...args: Parameters<ApiCoreClientEvents[Event]>): boolean;
}

export class ApiCoreClient extends EventEmitter {
  readonly SERVICES = getServices();

  private _sessionToken: string | null = null;
  private _actor: Actor | null = null;

  private _actorCreatePromise: Promise<Actor> | null = null;

  readonly socket: SocketConnection;
  readonly roomStateStore: RoomStateStore;
  readonly cacheApi: RoomStateCacheApi;

  private socketReadyPromise: Promise<void>;

  private connectedRoomRoute: string | null = null;

  get actor() {
    return this._actor;
  }

  private set actor(val: Actor | null) {
    this._actor = val;
    this.emit('actorChange', val);
  }

  patchActor = (updates: Partial<Actor>) => {
    if (!this._actor) return;
    this.actor = { ...this._actor, ...updates };
  };

  get sessionToken() {
    return this._sessionToken;
  }

  get roomId() {
    return this.roomStateStore.getState().id;
  }

  get currentRoomRoute() {
    return this.connectedRoomRoute;
  }

  constructor() {
    super();
    this.setMaxListeners(10000);
    const storedSessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
    if (storedSessionToken) {
      this._sessionToken = storedSessionToken;
    }
    this.roomStateStore = roomStateStore;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.socket = new SocketConnection();
    this.socketReadyPromise = new Promise((resolve) => {
      this.socket.on('connected', resolve);
    });
    this.socket.on('connected', this.handleSocketReconnect);

    // Real-time Moderator Actions
    this.socket.on('message:forceMute', () => {
      import('@src/media').then(({ media }) => {
        media.stopMicrophone();
        toast.error("A moderator has muted your microphone");
      });
    });

    this.socket.on('message:forceDisableCamera', () => {
      import('@src/media').then(({ media }) => {
        media.stopCamera();
        toast.error("A moderator has disabled your camera");
      });
    });

    this.socket.on('message:roleUpdated', (msg: any) => {
       const { targetActorId, newRole } = msg.payload;
       this.cacheApi.updateUserRole({ id: targetActorId.toString(), role: newRole });
       
       // If I am the target, notify me
       if (this.actor?.actorId.toString() === targetActorId.toString()) {
           toast.success(`Your role has been updated to ${newRole}`);
       }
    });

    this.cacheApi = this.roomStateStore.getState().cacheApi;
  }

  // Internal Socket Management
  private handleSocketReconnect = () => {
    if (this.connectedRoomRoute) {
      const wasObserver = this.cacheApi.getCurrentUser()?.isObserver;
      // we are connected to a room -- reset the room state
      // to get the latest updates and rejoin!
      this.connectToMeeting(this.connectedRoomRoute, wasObserver);
    }
  };

  // Internal Actor State Management

  private createActor = async () => {
    // bail early if we have one already
    if (this._actor) {
      return this._actor;
    }
    const { actor, sessionToken } = await this.post<{ actor: any; sessionToken: string }>(
      '/stub_user',
      {
        source: getRef(),
      },
      this.SERVICES.api
    );

    this.actor = actor;

    this._sessionToken = sessionToken;

    localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);

    return actor;
  };

  /**
   * Ensures an actor is assigned to this client in a synchronized way.
   * @param forceCreate If true, will create a guest/stub user if no actor exists.
   */
  private ensureActor = async (forceCreate = true) => {
    // if an actor exists, we're done.
    if (this.actor) {
      return this.actor;
    }
    if (this._sessionToken) {
      // we have an active session, try to restore actor
      try {
        const actorResponse = await this.get<{ actor: Actor }>('/actor', this.SERVICES.api);
        this.actor = actorResponse.actor;
        return this.actor;
      } catch (err) {
        // session token is invalid or expired
        this._sessionToken = null;
        localStorage.removeItem(SESSION_TOKEN_KEY);
      }
    }
    
    // If not forced, return null (don't create stub user yet)
    if (!forceCreate) {
      return null;
    }

    if (!this._actorCreatePromise) {
      // create and assign the promise, then clear it after it is finished.
      this._actorCreatePromise = this.createActor().finally(() => {
        this._actorCreatePromise = null;
      });
    }
    return this._actorCreatePromise;
  };

  /** Request method wrapper for mandating an actor before making a request. */
  requireActor = <Args extends any[], Ret extends any>(handler: (...args: Args) => Ret): AsyncMiddleware<Args, Ret> => {
    return (async (...args: Args) => {
      const actor = await this.ensureActor(true); // Always force create for required actions
      if (!actor) throw new Error("Actor required");
      return handler(...args);
    }) as unknown as AsyncMiddleware<Args, Ret>;
  };

  // Authentication

  login = () => {
    return this.ensureActor(true);
  };

  loginWithCredentials = async (email: string, password: string) => {
    const { actor, sessionToken } = await this.post<{ actor: any; sessionToken: string }>(
      '/auth/login',
      { email, password },
      this.SERVICES.api
    );
    this.actor = actor;
    this._sessionToken = sessionToken;
    localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
    return actor;
  };

  signupWithCredentials = async (email: string, password: string, displayName: string, firstName?: string, lastName?: string) => {
    const { actor, sessionToken } = await this.post<{ actor: any; sessionToken: string }>(
      '/auth/signup',
      { email, password, displayName, firstName, lastName },
      this.SERVICES.api
    );
    this.actor = actor;
    this._sessionToken = sessionToken;
    localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
    return actor;
  };

  updateActor = this.requireActor(async (data: Partial<Actor>) => {
    const { actor } = await this.post<{ actor: Actor }>('/actor/update', data, this.SERVICES.api);
    if (actor) {
      this.actor = actor; // Update local state
      return actor;
    }
    return this.actor!;
  });

  // Admin
  listUsers = this.requireActor(async (page: number, limit: number) => {
    return this.post<{ users: Actor[]; total: number; page: number }>('/admin/users', { page, limit }, this.SERVICES.api);
  });

  setRole = this.requireActor(async (targetActorId: string, role: string) => {
    return this.post<{ success: boolean }>('/admin/set_role', { targetActorId, role }, this.SERVICES.api);
  });

  setRoomRole = this.requireActor(async (roomRoute: string, targetActorId: string, role: string) => {
    return this.post<{ success: boolean }>('/room/set_role', { roomRoute, targetActorId, newRole: role }, this.SERVICES.api);
  });

  muteParticipant = this.requireActor(async (roomRoute: string, targetActorId: string) => {
    return this.post<{ success: boolean }>('/room/mute_participant', { roomRoute, targetActorId }, this.SERVICES.api);
  });

  disableParticipantCamera = this.requireActor(async (roomRoute: string, targetActorId: string) => {
    return this.post<{ success: boolean }>('/room/disable_camera', { roomRoute, targetActorId }, this.SERVICES.api);
  });

  setIneaVoiceAgent = this.requireActor(async (roomRoute: string, enabled: boolean) => {
    return this.post<{ success: boolean; enabled: boolean; roomName: string; agentName: string }>(
      '/room/inea_voice_agent',
      { roomRoute, enabled },
      this.SERVICES.api,
    );
  });

  requestInea = this.requireActor(async (roomRoute: string, prompt: string, responseLanguage?: string) => {
    return this.post<{ success: boolean; answer: string; category: string; responseLanguage?: string; remainingQuota?: number }>(
      '/room/inea_request',
      { roomRoute, prompt, responseLanguage },
      this.SERVICES.api,
    );
  });

  getIneaHistory = this.requireActor(async (roomRoute: string) => {
    return this.post<{ success: boolean; requests: Array<any> }>(
      '/room/inea_history',
      { roomRoute },
      this.SERVICES.api,
    );
  });

  summarizeRoomTimeline = this.requireActor(async (roomRoute: string) => {
    return this.post<{ success: boolean; result: any }>(
      '/room/timeline_summarize',
      { roomRoute },
      this.SERVICES.api,
    );
  });

  getRoomTimelineContext = this.requireActor(async (roomRoute: string) => {
    return this.post<{ success: boolean; result: any }>(
      '/room/timeline_context',
      { roomRoute },
      this.SERVICES.api,
    );
  });

  listMyRooms = this.requireActor(async (page: number, limit: number) => {
    return this.post<{ rooms: Room[]; total: number; page: number }>('/my_rooms', { page, limit }, this.SERVICES.api);
  });

  listSharedRooms = this.requireActor(async (page: number, limit: number) => {
    return this.post<{ rooms: Room[]; total: number; page: number }>('/my_shared_rooms', { page, limit }, this.SERVICES.api);
  });

  listRooms = this.requireActor(async (page: number, limit: number) => {
    return this.post<{ rooms: Room[]; total: number; page: number }>('/admin/rooms', { page, limit }, this.SERVICES.api);
  });

  shareRoom = this.requireActor(async (roomId: number, targetUsername: string) => {
    return this.post<{ message: string }>('/share_room', { roomId, targetUsername }, this.SERVICES.api);
  });

  getSharedUsers = this.requireActor(async (roomId: number) => {
    return this.post<{ users: Actor[] }>('/get_shared_users', { roomId }, this.SERVICES.api);
  });

  removeSharedUser = this.requireActor(async (roomId: number, targetActorId: string) => {
    return this.post<{ message: string }>('/remove_shared_user', { roomId, targetActorId }, this.SERVICES.api);
  });

  toggleRoomStatus = this.requireActor(async (roomId: number, isOpen: boolean) => {
    return this.post<{ message: string }>('/toggle_status', { roomId, isOpen }, this.SERVICES.api);
  });

  deleteRoom = this.requireActor(async (roomId: string) => {
    return this.post<{ success: boolean }>('/admin/delete_room', { roomId }, this.SERVICES.api);
  });

  verifyUser = this.requireActor(async (targetActorId: string) => {
    return this.post<{ success: boolean }>('/admin/verify_user', { targetActorId }, this.SERVICES.api);
  });

  banUser = this.requireActor(async (targetActorId: string) => {
    return this.post<{ success: boolean }>('/admin/ban_user', { targetActorId }, this.SERVICES.api);
  });

  unbanUser = this.requireActor(async (targetActorId: string) => {
    return this.post<{ success: boolean }>('/admin/unban_user', { targetActorId }, this.SERVICES.api);
  });

  deleteUser = this.requireActor(async (targetActorId: string, hard = false) => {
    return this.post<{ success: boolean }>('/admin/delete_user', { targetActorId, hard }, this.SERVICES.api);
  });

  listTemplates = this.requireActor(async () => {
    return this.post<{ templates: any[] }>('/admin/templates', {}, this.SERVICES.api);
  });

  updateTemplate = this.requireActor(async (templateName: string, templateData: any) => {
    return this.post<{ template: any }>('/update_template', { templateName, templateData }, this.SERVICES.api);
  });

  createTemplate = this.requireActor(async (templateName: string, templateData: any) => {
    return this.post<{ template: any }>('/create_template', { templateName, templateData }, this.SERVICES.api);
  });

  deleteTemplate = this.requireActor(async (templateName: string) => {
    return this.post<{ success: boolean }>('/delete_template', { templateName }, this.SERVICES.api);
  });

  getGlobalConfig = this.requireActor(async (key: string) => {
    return this.post<{ value: string | null }>('/admin/config/get', { key }, this.SERVICES.api);
  });

  setGlobalConfig = this.requireActor(async (key: string, value: string) => {
    return this.post<{ success: boolean }>('/admin/config/set', { key, value }, this.SERVICES.api);
  });

  // Cron Management
  listCrons = this.requireActor(async () => {
    return this.post<{ jobs: import('./types').CronJob[] }>('/admin/crons/list', {}, this.SERVICES.api);
  });

  toggleCron = this.requireActor(async (name: string, running: boolean) => {
    return this.post<{ success: boolean }>('/admin/crons/toggle', { name, running }, this.SERVICES.api);
  });

  runCron = this.requireActor(async (name: string) => {
    return this.post<{ success: boolean }>('/admin/crons/run', { name }, this.SERVICES.api, 120000);
  });

  // Groups
  createGroup = this.requireActor(async (name: string, secret?: string) => {
    return this.post<{ group: Group }>('/groups/create', { name, secret }, this.SERVICES.api);
  });

  updateGroup = this.requireActor(async (groupId: number, name?: string, secret?: string) => {
    return this.post<{ group: Group }>('/groups/update', { groupId, name, secret }, this.SERVICES.api);
  });

  deleteGroup = this.requireActor(async (groupId: number) => {
    return this.post<{ success: boolean }>('/groups/delete', { groupId }, this.SERVICES.api);
  });

  listGroups = this.requireActor(async () => {
    return this.post<{ groups: Group[] }>('/groups/list', {}, this.SERVICES.api);
  });

  getGroupDetails = async (groupId: number) => {
    return this.post<{ group: Group }>('/groups/details', { groupId }, this.SERVICES.api);
  };

  updateGroupRooms = this.requireActor(async (groupId: number, roomIds: number[]) => {
    return this.post<{ success: boolean }>('/groups/update_rooms', { groupId, roomIds }, this.SERVICES.api);
  });

  getGroupMembers = this.requireActor(async (groupId: number) => {
    return this.post<{ members: Actor[] }>('/groups/members', { groupId }, this.SERVICES.api);
  });

  removeGroupMember = this.requireActor(async (groupId: number, actorId: number) => {
    return this.post<{ success: boolean }>('/groups/remove_member', { groupId, actorId }, this.SERVICES.api);
  });

  joinGroup = this.requireActor(async (groupId?: number, secret?: string, name?: string) => {
    return this.post<{ success: boolean }>('/groups/join', { groupId, secret, name }, this.SERVICES.api);
  });

  listMyGroupRooms = this.requireActor(async () => {
    return this.post<{ rooms: Room[] }>('/groups/my_rooms', {}, this.SERVICES.api);
  });

  listWallpapers = this.requireActor(async () => {
    return this.get<{ wallpapers: any[] }>('/list_wallpapers', this.SERVICES.api);
  });

  uploadWallpaper = this.requireActor(async (file: File) => {
    return this.request<{ wallpaper: any }>({
      method: 'POST',
      endpoint: '/upload_wallpaper',
      data: { file },
      service: this.SERVICES.api,
      contentType: 'multipart/form-data',
    });
  });

  logout = () => {
    this.actor = null;
    this._sessionToken = null;
    this._actorCreatePromise = null;
    localStorage.removeItem(SESSION_TOKEN_KEY);
    // so the app remains functional for basic browsing/widget creation
    this.ensureActor(false);
  };

  // Core Meeting Functionality

  createMeeting = this.requireActor((templateName: MeetingTemplateName, template?: any) => {
    return this.post<{ newMeeting: any }>('/create_meeting', { templateName, template, source: getRef() }, this.SERVICES.api);
  });

  async testTemplate(templateName: string, templateData?: any) {
    return this.post<{ newMeeting: any }>('/test_template', { templateName, template: templateData }, this.SERVICES.api);
  }

  updateRoom = this.requireActor(async (roomId: number, displayName?: string, state?: any) => {
    return this.post<{ success: boolean }>('/update_room', { roomId, displayName, state }, this.SERVICES.api);
  });

  /**
   * Connects to a meeting room, provisions a media token, joins the socket channel... the whole
   * 9 yards.
   * @returns the media token to join AV session
   */
  connectToMeeting = async (roomRoute: string, isObserver?: boolean) => {
    const actor = await this.ensureActor(true);
    if (!actor || !this._sessionToken) {
      throw new ApiError({ success: false, errorCode: ErrorCodes.NOT_LOGGED_IN, message: 'Authentication required' }, 401);
    }

    const effectiveIsObserver = typeof isObserver === 'boolean' ? isObserver : false;
    // retrieve a media token first - this tells us the room exists
    const { token, roomId, actorId } = await this.post<{ token: string; roomId: number; actorId: number }>(
      '/logged_in_join_room',
      { roomRoute, isObserver: effectiveIsObserver },
      this.SERVICES.api
    );

    this.socket.setConnectionInfo(token, roomId, actorId);

    // Ensure socket connection is started
    if (this.SERVICES.hermes.ws) {
        this.socket.connect(this.SERVICES.hermes.ws);
    }

    // wait for the socket to be connected ... this could be smoother
    await this.socketReadyPromise;

    // authenticate and join the room via websocket
    const authResponse = await this.socket.sendAndWaitForResponse<IncomingAuthResponseMessage>(
      {
        kind: 'auth',
        payload: {
          roomRoute,
          // requireActor ensures this
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          token: this.sessionToken!,
          isObserver: effectiveIsObserver,
          actorId: this.actor?.actorId,
        },
      },
      // 10 second timeout; it can be a large payload
      10 * 1000
    );
    // initialize the state store
    this.cacheApi.reset();
    this.cacheApi.initialize(authResponse);

    this.connectedRoomRoute = roomRoute;

    return token;
  };

  getPublicRoomInfo = async (roomRoute: string) => {
    return this.post<{ isOpen: boolean; displayName: string; id: number }>('/get_public_room_info', { roomRoute }, this.SERVICES.api);
  };
   
  getMediaProviders = async () => {
    const mediaProviders = await this.get<{
      [key: string]: any;
    }>('/media_providers', this.SERVICES.api);
    return mediaProviders;
  };

  leaveMeeting = () => {
    this.cacheApi.reset();
    this.connectedRoomRoute = null;
    // TODO: socket leave event
  };

  // Generic HTTP methods

  async post<Response = Record<string, unknown>>(endpoint: string, data: any = {}, service: Service, timeoutMs?: number) {
    return this.request<Response>({
      method: 'POST',
      endpoint,
      data: data,
      service,
      timeoutMs,
    });
  }

  async get<Response = Record<string, unknown>>(endpoint: string, service: Service) {
    return this.request<Response>({
      method: 'GET',
      endpoint,
      service,
    });
  }

  /**
   * Makes an API request and throws an ApiError if it fails
   */
  async request<Response>(opts: {
    method: string;
    endpoint: string;
    data?: any;
    service: Service;
    contentType?: string;
    timeoutMs?: number;
  }) {
    const { service, method, endpoint, data, timeoutMs } = opts;

    // timeout requests after 15s by default, or use provided timeout
    const abortController = new AbortController();
    const timeout = setTimeout(() => abortController.abort(), timeoutMs || 15_000);

    try {
      const analyticsRef = getRef();
      const response = await fetch(`${service.http}${endpoint}`, {
        method,
        headers: {
          ...(opts.contentType === 'multipart/form-data'
            ? {}
            : {
              'Content-Type': opts.contentType || 'application/json',
            }),
          ...(analyticsRef ? { 'X-Analytics-Ref': analyticsRef } : {}),
          ...this.getAuthHeaders(),
        },
        body: this.prepareBody(data, opts.contentType || 'application/json'),
        signal: abortController.signal,
      });
      clearTimeout(timeout);

      const raw = await response.text();
      let body: (BaseResponse & Response) | ErrorResponse;
      try {
        body = (raw ? JSON.parse(raw) : {}) as any;
      } catch {
        const details = raw ? raw.slice(0, 500) : undefined;
        throw new ApiError(
          {
            errorCode: ErrorCodes.UNEXPECTED,
            message: `${i18n.t('error.api.UNEXPECTED.message')} (Invalid JSON response)`,
            success: false,
            details,
          },
          response.status
        );
      }

      if (typeof (body as any)?.success !== 'boolean') {
        const details = raw ? raw.slice(0, 500) : undefined;
        throw new ApiError(
          {
            errorCode: ErrorCodes.UNEXPECTED,
            message: `${i18n.t('error.api.UNEXPECTED.message')} (Malformed response)`,
            success: false,
            details,
          },
          response.status
        );
      }

      if (!body.success) {
        throw new ApiError(body, response.status);
      }

      return body;
    } catch (err) {
      if (err instanceof ApiError) throw err;

      logger.error(err);
      throw new ApiError({
        errorCode: ErrorCodes.UNEXPECTED,
        message: `${i18n.t('error.api.UNEXPECTED.message')} (${err instanceof Error ? err.message : String(err)})`,
        success: false,
      });
    }
  }

  private prepareBody = (data: any, contentType: string) => {
    if (contentType === 'application/json') {
      return JSON.stringify(data);
    }
    if (contentType === 'multipart/form-data') {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });
      return formData;
    }
    return data;
  };

  private getAuthHeaders(): { Authorization: string } | Record<string, unknown> {
    return this._sessionToken
      ? {
        Authorization: `Bearer ${btoa(this._sessionToken)}`,
      }
      : {};
  }
}

type AsyncMiddleware<Args extends any[], Ret extends any> = Ret extends Promise<infer T>
  ? (...args: Args) => Promise<T>
  : (...args: Args) => Promise<Ret>;
